import {
  Button,
  DateTimePickerWithRange,
  FancyMultiSelect,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectType,
  SelectValue,
  Switch,
  Textarea,
  TimeDurationPicker,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import {
  MutateVirtualAtonFormValues,
  virtualAtonMessageTypes,
} from './atonForm';
import { PiMapPin, PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { DateRange } from 'react-day-picker';
import { useFetchPSSWithType } from '@ais-ui/api';
import { DrawGeometryType, latLonToShape } from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useTimeZoneContext } from '@ais-ui/utils';

interface ScheduleFormFieldProps {
  form: UseFormReturn<MutateVirtualAtonFormValues>;
}

interface SizeFormFieldProps {
  form: UseFormReturn<MutateVirtualAtonFormValues>;
}

interface DateTimePickerRangeInputProps {
  value: { from: Date | undefined; to: Date | undefined };
  index: number;
  onScheduleChange: (index: number, date: DateRange | undefined) => void;
}

const DateTimePickerRangeInput = ({
  value,
  index,
  onScheduleChange,
}: DateTimePickerRangeInputProps) => {
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  const [date, setDate] = React.useState<DateRange | undefined>(undefined);

  useEffect(() => {
    if (value.from && value.to) {
      setDate({ from: value.from, to: value.to });
    }
  }, [value.from, value.to]);

  useEffect(() => {
    onScheduleChange(index, date);
  }, [date, index, onScheduleChange]);

  if (!date && value.from && value.to) {
    return null; // We have to return null in this case, as date needs to be set before initializing the DateTimePickerWithRange
  }

  return (
    <DateTimePickerWithRange
      className="w-full"
      date={date}
      setDate={setDate}
      timeZone={timeZone}
      dateTimeFormat={userDateFormat}
      dateLimit="future"
    ></DateTimePickerWithRange>
  );
};

const SizeFormField = ({ form }: SizeFormFieldProps) => {
  const { messages } = useLocalization();

  const [chooseSize, setChooseSize] = React.useState<boolean>(false);
  return (
    <div className="flex flex-col space-y-4">
      <div className="flex space-x-4 items-center">
        <Switch
          checked={chooseSize}
          onCheckedChange={(value) => setChooseSize(value)}
        ></Switch>
        <FormLabel>{messages.atonVirtualDialogChooseSizeLabel}</FormLabel>
      </div>
      {chooseSize && (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="sizeA"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {messages.atonVirtualDialogSizeALabel} *
                  </FormLabel>
                  <InfoIconTooltip>
                    {messages.atonVirtualDialogSizeATooltip}
                  </InfoIconTooltip>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    id="virtual-aton-size-a-field"
                    type="number"
                    placeholder={messages.atonVirtualDialogSizeAPlaceholder}
                    autoComplete="off"
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        const numberValue = parseFloat(e.target.value);
                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sizeB"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {messages.atonVirtualDialogSizeBLabel} *
                  </FormLabel>
                  <InfoIconTooltip>
                    {messages.atonVirtualDialogSizeBTooltip}
                  </InfoIconTooltip>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    id="virtual-aton-size-b-field"
                    type="number"
                    placeholder={messages.atonVirtualDialogSizeBPlaceholder}
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        const numberValue = parseFloat(e.target.value);
                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sizeC"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {messages.atonVirtualDialogSizeCLabel} *
                  </FormLabel>
                  <InfoIconTooltip>
                    {messages.atonVirtualDialogSizeCTooltip}
                  </InfoIconTooltip>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    id="virtual-aton-size-c-field"
                    type="number"
                    placeholder={messages.atonVirtualDialogSizeCPlaceholder}
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        const numberValue = parseFloat(e.target.value);
                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sizeD"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {messages.atonVirtualDialogSizeDLabel} *
                  </FormLabel>
                  <InfoIconTooltip>
                    {messages.atonVirtualDialogSizeDTooltip}
                  </InfoIconTooltip>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    id="virtual-aton-size-d-field"
                    type="number"
                    placeholder={messages.atonVirtualDialogSizeDPlaceholder}
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        const numberValue = parseFloat(e.target.value);
                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};

const ScheduleFormField = ({ form }: ScheduleFormFieldProps) => {
  const { messages } = useLocalization();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'schedule',
  });

  const onScheduleChange = useCallback(
    (index: number, date: DateRange | undefined) => {
      form.setValue(`schedule.${index}.start`, date?.from);
      form.setValue(`schedule.${index}.end`, date?.to);
    },
    [form]
  );

  return (
    <FormField
      key="schedule"
      control={form.control}
      name="schedule"
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between">
            <div className="flex space-x-4 items-center">
              <FormLabel>{messages.atonVirtualDialogScheduleLabel}</FormLabel>
            </div>
            <InfoIconTooltip>
              {messages.atonVirtualDialogScheduleTooltip}
            </InfoIconTooltip>
          </div>

          <FormControl onChange={() => form.clearErrors()}>
            <>
              {fields.map((schedule, index) => (
                <div key={`${index}`} className="flex space-x-2">
                  <Button
                    type="button"
                    size="icon"
                    disabled={true}
                    variant="ghost"
                    className="h-9 w-9 flex-shrink-0"
                    onClick={() => remove(index)}
                  >
                    {index + 1}
                  </Button>
                  <DateTimePickerRangeInput
                    value={{ from: schedule.start, to: schedule.end }}
                    index={index}
                    onScheduleChange={onScheduleChange}
                  ></DateTimePickerRangeInput>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-9 w-9 flex-shrink-0 flex-grow-0"
                        onClick={() => remove(index)}
                      >
                        <PiMinusThin className="h-4 w-4"></PiMinusThin>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {messages.atonVirtualDialogScheduleRemoveTooltip}
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    id="virtual-aton-add-schedule-button"
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9"
                    onClick={() =>
                      append({
                        start: undefined,
                        end: undefined,
                      })
                    }
                  >
                    <PiPlusThin className="h-4 w-4"></PiPlusThin>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {messages.atonVirtualDialogScheduleAddTooltip}
                </TooltipContent>
              </Tooltip>
            </>
          </FormControl>
          {form.formState.errors.schedule && (
            <p className="text-sm font-medium text-destructive">
              {messages.atonVirtualDialogScheduleGenericFormError}
            </p>
          )}
        </FormItem>
      )}
    />
  );
};

interface MutateFormProps {
  form: UseFormReturn<MutateVirtualAtonFormValues>;
  onStartDrawing: (geometryType: DrawGeometryType, shape?: GeoJson) => void;
}

export const MutateVirtualForm = ({
  form,
  onStartDrawing,
}: MutateFormProps) => {
  const { messages } = useLocalization();
  const atonTypeOptions = [
    {
      label: 'Virtual',
      value: '1',
    },
    {
      label: 'Synthetic',
      value: '2',
    },
  ];

  let existingShape: GeoJson | undefined = undefined;
  if (form.getValues('longitude') && form.getValues('latitude')) {
    existingShape = latLonToShape(
      form.getValues('latitude'),
      form.getValues('longitude')
    );
  }

  const { data: pss } = useFetchPSSWithType('aton');

  const allowedPSS: SelectType[] = useMemo(() => {
    if (!pss || !pss.records) {
      return [];
    }

    const filteredPSS = pss.records.filter(
      (pssItem) => pssItem.allowed === true
    );

    return filteredPSS.map(
      (pssItem) =>
        ({
          label: pssItem.name,
          value: pssItem.uuid,
        } as SelectType)
    );
  }, [pss]);

  const allowedPSSWithNone: SelectType[] = useMemo(() => {
    const allowedPSSWithNone = [
      { value: 'no-chain', label: 'No chained pss' },
      ...allowedPSS,
    ];
    return allowedPSSWithNone;
  }, [allowedPSS]);

  return (
    <div className="space-y-8">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonVirtualDialogNameLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                id="virtual-aton-name-field"
                type="text"
                placeholder={messages.atonVirtualDialogNamePlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="mmsi"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonVirtualDialogMmsiLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                id="virtual-aton-mmsi-field"
                type="number"
                placeholder={messages.atonVirtualDialogMmsiPlaceholder}
                autoComplete="off"
                {...field}
                onChange={(e) => {
                  if (!e.target.value) {
                    field.onChange('');
                  } else {
                    const numberValue = parseFloat(e.target.value);
                    field.onChange(numberValue);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <SizeFormField form={form}></SizeFormField>
      <div className="flex space-x-4">
        <FormField
          control={form.control}
          name="latitude"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>
                  {messages.atonVirtualDialogLatitudeLabel} *
                </FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Input
                  id="virtual-aton-latitude-field"
                  type="number"
                  placeholder={messages.atonVirtualDialogLatitudePlaceholder}
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseFloat(e.target.value);
                      const roundedValue =
                        Math.round(numberValue * 100000) / 100000;
                      field.onChange(roundedValue);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="longitude"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>
                  {messages.atonVirtualDialogLongitudeLabel} *
                </FormLabel>
              </div>
              <div className="flex space-x-4">
                <div className="space-y-2">
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      id="virtual-aton-longitude-field"
                      type="number"
                      placeholder={
                        messages.atonVirtualDialogLongitudePlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          const numberValue = parseFloat(e.target.value);
                          const roundedValue =
                            Math.round(numberValue * 100000) / 100000;
                          field.onChange(roundedValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
                <Button
                  id="virtual-aton-choose-location-button"
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 flex-shrink-0 flex-grow-0"
                  onClick={() => onStartDrawing('Point', existingShape)}
                >
                  <PiMapPin className="h-4 w-4"></PiMapPin>
                </Button>
              </div>
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonVirtualDialogDescriptionLabel}
              </FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Textarea id="virtual-aton-description" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonVirtualDialogMessageTypeLabel} *
              </FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                defaultValue="0"
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    id="virtual-aton-message-type-select"
                    placeholder={
                      messages.atonVirtualDialogMessageTypePlaceholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {virtualAtonMessageTypes.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="virtual"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonVirtualDialogTypeLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select onValueChange={field.onChange} defaultValue="1">
                <SelectTrigger className="w-full">
                  <SelectValue
                    id="virtual-aton-aton-type-select"
                    placeholder={messages.atonVirtualDialogTypePlaceholder}
                  />
                </SelectTrigger>
                <SelectContent>
                  {atonTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        key="broadcastInterval"
        control={form.control}
        name="broadcastInterval"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonVirtualDialogBroadcastIntervalLabel} *
              </FormLabel>
              <InfoIconTooltip>
                {messages.atonVirtualDialogBroadcastIntervalTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <TimeDurationPicker
                value={field.value}
                onChange={field.onChange}
                format="HH:MM:SS"
              ></TimeDurationPicker>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="warningTimeout"
        control={form.control}
        name="warningTimeout"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonVirtualDialogWarningTimeoutLabel} *
              </FormLabel>
              <InfoIconTooltip>
                {messages.atonVirtualDialogWarningTimeoutTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <TimeDurationPicker
                value={field.value}
                onChange={field.onChange}
                format="HH:MM:SS"
              ></TimeDurationPicker>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="failureTimeout"
        control={form.control}
        name="failureTimeout"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonVirtualDialogFailureTimeoutLabel} *
              </FormLabel>
              <InfoIconTooltip>
                {messages.atonVirtualDialogFailureTimeoutTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <TimeDurationPicker
                value={field.value}
                onChange={field.onChange}
                format="HH:MM:SS"
              ></TimeDurationPicker>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{messages.atonVirtualDialogShareLabel}</FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="pss"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonVirtualDialogPssLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <FancyMultiSelect
                value={field.value}
                onChange={field.onChange}
                availableSelectables={allowedPSS}
                placeholder={messages.atonMutualVirtualFormPssPlaceholder}
              ></FancyMultiSelect>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="chained"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonVirtualDialogChainedPssLabel}</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                defaultValue=""
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    id="virtual-aton-chained-pss-select"
                    defaultValue=""
                    placeholder={
                      messages.atonVirtualDialogChainedPssPlaceholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {allowedPSSWithNone.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <ScheduleFormField form={form}></ScheduleFormField>
    </div>
  );
};
